/**
 * SHELL WEB — anatomie des invariants §24.9sexies-ter en DONNÉES : marque de rail (a), topbar (b),
 * item de nav actif (g), gamme de BOUTONS. Les primitives `ui-web/shell` et `ui-web/button` les lisent.
 *
 * ⚠️ CE QUI N'EST PAS ICI EST LIBRE (doctrine « jamais un gabarit rigide », décision utilisateur
 * 2026-08-05) : largeur de rail, contenu de topbar, layout de page, densités — chaque app compose.
 * Ici ne vivent que les décisions déjà écrites au SPEC, pour qu'elles cessent d'être recopiées.
 */

/** Largeur CANONIQUE du rail (RETENU 2026-08-05, décision utilisateur : une seule largeur partout —
 *  les apps étaient à 224/240/260 selon l'histoire de chacune). */
export const RAIL_WIDTH = 240;

/** Sous cette largeur d'écran, le rail devient un TIROIR hors écran ouvert au burger de la topbar
 *  (passe responsive 2026-08-24 : un rail fixe de 240 px mangeait 60 % d'un téléphone). 900 couvre les
 *  téléphones ET les tablettes portrait — à 768 px, 240 px de rail ne laissent que 528 px aux tableaux. */
export const RAIL_BREAKPOINT = 900;

/**
 * RESPIRATION DU RAIL — le rembourrage de l'`<aside>` lui-même, relevé sur la maquette back-office :
 * 18 en haut, 12 sur les côtés et en bas.
 *
 * ⚠️ CE N'EST PAS UN DÉTAIL D'ESPACEMENT, c'est ce qui met les FILETS EN RETRAIT au lieu de les faire
 * courir d'un bord à l'autre — un rail rembourré se lit comme une identité posée dans le menu, pas
 * comme une barre séparée.
 *
 * ⚠️ IL VIVAIT DANS LE BACK-OFFICE, et il y était le seul (jusqu'au 2026-09-08). Les trois autres
 * rails rembourraient leurs propres blocs, chacun à sa valeur : mesuré au navigateur, l'EDI posait sa
 * marque à 16, ses items à 22 et son avatar à 12 — TROIS colonnes différentes dans le MÊME rail. Le
 * back-office, lui, aligne items et avatar sur 24. Décision utilisateur : « on aligne toutes les apps
 * sur le back-office, ça donne un aspect plus aéré ».
 *
 * ⚠️ IL SE POSE SUR L'ASIDE, ET LES BLOCS INTÉRIEURS N'EN POSENT PLUS. Ajouter celui-ci sans retirer
 * les leurs les additionnerait — c'est la seule façon de se tromper ici, et elle est silencieuse.
 */
export const RAIL_PADDING = '18px 12px 12px';

/**
 * Marque de rail (§24.9sexies-ter a — référence : rail admin du Store privé).
 *
 * ⚠️ DEUX AGENCEMENTS, UNE SEULE PRIMITIVE (2026-09-04). Le Store, la Console et l'EDI posent le RÔLE
 * de la surface AU-DESSUS du nom (« ÉDITEUR · STAFF » / « Orgazone Console ») ; la maquette du
 * back-office fait l'inverse — le PRODUIT d'abord (« Sliderz »), sa surface dessous en mono capitales
 * (« BACKOFFICE »). Les deux disent la même chose dans l'ordre inverse, et le choix appartient à la
 * maquette de chaque produit : c'est un `layout`, jamais une seconde marque de rail recopiée.
 */
export const RAIL_BRAND = {
  /**
   * ⚠️ `16px 16px 12px` → `2px 8px 12px` (2026-09-08) : l'aside porte désormais `RAIL_PADDING`, donc
   * 18 en haut et 12 sur les côtés viennent de lui. Garder les anciennes valeurs les AURAIT ADDITIONNÉES
   * — marque à 28 du bord au lieu de 20, tête de rail poussée à 34 du haut. Les deux valeurs retenues
   * sont celles de l'agencement `subtitle` du back-office, pour la même raison qui y est déjà écrite.
   */
  padding: '2px 8px 12px',
  gap: 10,
  logoBox: 30,
  logoRadius: 8,
  surtitleSize: 10,
  surtitleSpacing: '.08em',
  nameSize: 14,
  nameWeight: 700,
  /** Agencement « produit d'abord » — métriques relevées sur la maquette back-office. */
  subtitle: {
    // Relevé : le bloc respire par le BAS (18) et à peine par le haut (2) — l'aside porte déjà 18.
    padding: '2px 8px 18px',
    gap: 10,
    logoWidth: 28,
    logoHeight: 29,
    nameSize: 15.5,
    nameSpacing: '-0.02em',
    subtitleSize: 9.5,
    subtitleSpacing: '.1em',
    /**
     * ⚠️ `normal`, ET C'EST UN RELEVÉ : la maquette back-office ne pose AUCUNE hauteur de ligne sur ce
     * bloc — ses deux lignes héritent donc du défaut du navigateur. Le rendu forçait `1.15`, ce qui
     * tassait le texte d'environ 1,3 px et rendait la tête de rail plus BASSE que la maquette (écart
     * signalé à l'œil avant d'être mesuré, 2026-09-05). Une mesure qu'on n'a pas relevée ne s'invente
     * pas : ici, la maquette dit « laisse hériter ».
     */
    lineHeight: 'normal',
    /**
     * LE FILET DE LA TÊTE DE RAIL — porté par le `<nav>`, jamais par le bloc de marque.
     *
     * ⚠️ POURQUOI SUR LE NAV : au relevé, le trait est un `border-top` du `<nav>`, donc il est EN
     * RETRAIT du rembourrage de l'aside. Posé sous la marque, il courait d'un bord à l'autre et
     * faisait de la tête une barre d'en-tête — plus haute à l'œil qu'elle ne l'est.
     *
     * ⚠️ POURQUOI UN JETON ET PAS TROIS DÉCLARATIONS DANS CHAQUE RAIL (règle de 2) : ces valeurs sont
     * un RELEVÉ de la maquette, et un relevé recopié dans deux apps se met à diverger au premier
     * ajustement — la structure du DOM reste à l'app, la MESURE vit ici, à côté des autres.
     */
    navEdge: { borderTop: '1px solid var(--sidebar-line)', marginTop: 2, paddingTop: 10 },
  },
} as const;

/**
 * RESPIRATION DE LA PAGE — le rembourrage de la colonne de contenu, sous la barre du haut.
 *
 * ⚠️ TROIS OUTILS, TROIS VALEURS, POUR LA MÊME STRUCTURE (relevé 2026-09-04) : le back-office posait
 * `20px 24px 40px`, la Console `26px 30px`, l'EDI `20px` partout. Aucun n'avait tort tout seul ;
 * ensemble ils faisaient trois alignements différents pour un même gabarit, et passer d'un outil à
 * l'autre décalait le titre de la page de quelques pixels sans raison.
 *
 * Valeurs de la maquette back-office. ⚠️ LE BAS EST PLUS GÉNÉREUX QUE LE HAUT, et ce n'est pas une
 * coquetterie : la colonne DÉFILE, donc la dernière carte doit pouvoir se détacher du bord de la
 * fenêtre au lieu de s'y coller.
 *
 * ⚠️ CE QUE CE JETON NE COUVRE PAS : la barre du haut, qui garde son propre rembourrage latéral (20)
 * — au relevé, ses CONTRÔLES commencent 8 px à gauche du titre de la page. C'est le dessin ; si on
 * veut un jour aligner les deux, c'est une décision à prendre, pas un écart à rattraper en silence.
 */
export const PAGE_PAD = { top: 24, side: 28, bottom: 60 } as const;

/**
 * DÉBORDEMENT PLEINE LARGEUR — la marge négative qui fait toucher un bandeau aux bords de la page.
 *
 * ⚠️ ELLE DOIT ÊTRE L'EXACT OPPOSÉ DE `PAGE_PAD`, ET C'EST POUR ÇA QU'ELLE EST CALCULÉE. Le bandeau
 * d'accueil du tableau de bord l'écrivait en dur (`-20px -24px`) : le jour où le rembourrage de page
 * est passé à 24/28 (2026-09-04), il a cessé de toucher l'en-tête et a laissé **4 px de fond visibles
 * au-dessus de lui** — une barre d'une autre couleur, et tout le contenu décalé vers le bas. Le
 * défaut est invisible au relecteur : les deux nombres sont justes séparément, c'est leur ACCORD qui
 * était rompu, et rien dans le code ne disait qu'ils devaient s'accorder.
 *
 * ⚠️ LE BAS N'EST PAS ANNULÉ : un bandeau déborde en HAUT et sur les CÔTÉS, jamais vers le bas — il
 * est suivi du contenu de la page, pas du bord de la fenêtre.
 */
/**
 * LARGEUR DE L'ASCENSEUR DE LA COLONNE DE CONTENU.
 *
 * ⚠️ ELLE EST UNE DIMENSION DE MISE EN PAGE, PAS UN DÉTAIL DE STYLE. Seul le bloc central défile
 * (§24.9sexies-ter c) : il est donc PLUS ÉTROIT que la barre du haut, exactement de cette largeur.
 * Sans en tenir compte, le dernier bouton de la barre et le bord droit du contenu ne tombent pas au
 * même endroit — un décalage de 10 px que l'œil voit sans savoir le nommer.
 * ⚠️ Elle est FIABLE parce que la charte l'impose (`::-webkit-scrollbar{width}`) : un ascenseur en
 * surimpression, laissé au système, aurait une largeur nulle un jour et douze pixels le lendemain.
 * ⚠️ Le corollaire est `scrollbar-gutter: stable` sur le défileur : la gouttière doit exister MÊME
 * quand la page est courte, sinon l'alignement ne tiendrait que sur les pages longues.
 */
export const SCROLLBAR_WIDTH = 10;

export const PAGE_BLEED = {
  top: -PAGE_PAD.top,
  side: -PAGE_PAD.side,
} as const;

/** La marge d'un bandeau qui touche les bords, avec sa respiration BASSE en pixels. */
export function pageBleedMargin(bottomGap: number): string {
  return `${PAGE_BLEED.top}px ${PAGE_BLEED.side}px ${bottomGap}px`;
}

/**
 * Topbar (§24.9sexies-ter b — référence EDI) + variante VITRÉE (maquette Console).
 *
 * ⚠️ HAUTEUR 60 ET REMBOURRAGE VERTICAL NUL (relevé maquette back-office, 2026-09-04). Elle valait
 * 52 px avec 8 px de rembourrage haut et bas, ce qui la rendait à 55 px au calcul : ni la valeur
 * annoncée, ni celle du dessin. La hauteur d'une barre se pose, elle ne se déduit pas d'un contenu
 * plus un rembourrage — sinon un contrôle un peu plus haut la fait grandir sans que personne l'ait
 * décidé.
 */
export const TOPBAR = {
  minHeight: 60,
  /**
   * ⚠️ `0 20px`, LE RELEVÉ DU BACK-OFFICE — et un retour en arrière assumé (2026-09-05).
   *
   * La version précédente faisait suivre `PAGE_PAD.side` et ajoutait la largeur de l'ascenseur à
   * droite, pour aligner la barre sur la colonne de contenu. Verdict de l'utilisateur devant
   * l'écran : « les icônes sont largement décalées du bord de la page ». 38 px à droite contre 20
   * dans le dessin, l'écart se voyait.
   *
   * ⚠️ LA MAQUETTE N'ALIGNE PAS CES DEUX CHOSES, ET C'EST DÉLIBÉRÉ CHEZ ELLE : son contenu est à
   * `24px 28px 60px` — exactement notre `PAGE_PAD` — et sa barre à `0 20px`. La barre du haut n'est
   * pas la page ; elle porte des CONTRÔLES, qui respirent moins qu'un titre. J'avais lu « les
   * paddings ne sont pas alignés sur la maquette » comme « alignez la barre sur le contenu », alors
   * que la phrase disait « nos valeurs ne sont pas celles du dessin ».
   *
   * ⚠️ SYMÉTRIQUE : plus de rattrapage d'ascenseur. Il servait l'alignement qu'on vient d'abandonner,
   * et sans lui la barre reste ce que le dessin montre.
   *
   * ⚠️ LA CONSOLE DIT 24 DANS SA PROPRE MAQUETTE (`padding:11px 24px`), et garde donc 4 px d'écart.
   * C'est CONNU et laissé tel quel : inventer un réglage par app pour 4 px que personne n'a signalés
   * ferait une exception permanente au profit d'un pixel. À trancher le jour où l'on regarde la
   * Console à l'écran, pas depuis un fichier de jetons.
   */
  padding: '0 20px',
  gap: 14,
  /**
   * ⚠️ LA BARRE SE REPLIE, ET C'EST UN RELEVÉ (maquette EDI : `flex-wrap:wrap`, `gap:8px 14px`).
   *
   * Sans ce repli, ses contrôles se compriment jusqu'à l'illisible quand la fenêtre se rétrécit — le
   * champ de recherche, le fil de page et la santé du service se disputent une seule ligne. La maquette
   * les laisse passer à la ligne, d'où un écart VERTICAL (8) distinct de l'écart horizontal (14) : un
   * `gap` unique de 14 entre deux lignes repliées aère trop, et la barre grandit d'un coup.
   */
  wrap: 'wrap',
  rowGap: 8,
  /**
   * TAILLE DES CONTRÔLES DE LA BARRE — capsule magasin, recherche, cloche, bascule de thème.
   * ⚠️ UNE SEULE, ET C'EST LE POINT : au relevé, les quatre font 38 px. La bascule de thème en
   * faisait 30, donc elle était visiblement plus petite que ses voisines — c'est ce qui donnait à la
   * barre son air de bric-à-brac. Le §24.9sexies-ter b écrivait « 30×30 » d'après la référence EDI ;
   * c'est la convention qui est corrigée, pas deux apps qu'on laisse diverger.
   */
  control: 38,
  controlIcon: 17,
  /**
   * TAILLE DE LA LOUPE DANS LA CAPSULE DE RECHERCHE.
   *
   * ⚠️ 🔴 LA CAPSULE ELLE-MÊME N'EST PAS ICI, ET C'EST LE POINT : la charte porte DÉJÀ sa recette
   * (`.field-search`, employée par sept écrans du back-office), au pixel près ce que la maquette EDI
   * demande. En la redécouvrant, j'en avais écrit une SECONDE — exactement le défaut que ce chantier
   * corrigeait. Ce qui manquait n'était pas la recette : c'était que la Console et l'EDI l'EMPLOIENT.
   *
   * ⚠️ 15 ET NON LE 14 DU DESSIN : c'est la valeur que les sept emplois existants dessinent déjà. Un
   * pixel d'écart ne vaut pas deux tailles de loupe dans la même plateforme.
   * ⚠️ Plus petite que `controlIcon` (17) parce que l'icône vit DANS le champ, à côté du texte, et non
   * seule au centre d'un bouton de 38.
   */
  searchIcon: 15,
  /** ⚠️ REPLI seulement : une app vitrée pose son voile via `--head` (cf. Topbar). Un papier à 82 %
   *  n'est pas translucide — il donne à la barre une couleur qui lui est propre. */
  glassPaperPct: 82,
  glassBlur: 'blur(14px) saturate(140%)',
} as const;


/*
 * ⚠️ `THEME_TOGGLE` A DISPARU (2026-09-04). La bascule de thème n'a aucune géométrie à elle : elle est
 * un CONTRÔLE DE LA BARRE comme la cloche, la recherche et la capsule magasin, et lui en donner une
 * l'avait rendue plus petite (30) que tous ses voisins (38). Sa taille vit dans `TOPBAR.control`.
 */

/** Item de nav du rail (§24.9sexies-ter g, RÉVISÉ 2026-08-05) : actif = APLAT marque-700 + texte blanc
 *  (style Store privé, retenu pour son contraste ~6:1 ; remplace la teinte+anneau). ⚠️ Divergence
 *  ASSUMÉE avec la tablette (teal-500, UI gelée) — le contraste desktop la justifie. */
export const RAIL_NAV = {
  // ⚠️ 38 → 40 et 13 → 14 (RELEVÉ SUR LA MAQUETTE back-office, 2026-09-04) : la valeur de 2026-08-05
  //    avait été estimée à l'œil (« un peu plats »), pas mesurée. Le dessin dit 40 px et 14 px pour un
  //    item de tête, 36 px et 13 px pour un enfant de groupe.
  /**
   * ⚠️ UNE HAUTEUR, PAS UN MINIMUM (relevé 2026-09-05). La maquette écrit `height:40px` : l'item est
   * VERROUILLÉ. Le rendu posait `min-height`, qui laisse la ligne GRANDIR dès qu'un contenu dépasse
   * — une pastille, un libellé qui passe à la ligne — et les items du rail cessaient alors d'avoir
   * tous la même épaisseur. Un rail dont les lignes respirent différemment selon leur contenu ne se
   * lit plus comme une liste.
   */
  height: 40,
  subHeight: 36,
  fontSize: 14,
  subFontSize: 13,
  gap: 11,
  iconSize: 19,
  weight: 450,
  weightActive: 600,
} as const;

/** Pied de rail (§24.9sexies-ter k — référence : back-office du Store privé, décision utilisateur
 *  2026-08-05) : avatar rond d'initiales sur marque-700 + nom fort + rôle + bouton icône Déconnexion. */
export const RAIL_FOOT = {
  /**
   * ⚠️ LA RESPIRATION AU-DESSUS DU FILET, relevée sur la maquette (`margin-top:8px` sur le conteneur
   * qui porte le `border-top`). Elle MANQUAIT au rendu : le pied remontait de 8 px et collait au lien
   * d'aide, qui paraissait poussé contre le bas du rail (signalé 2026-09-05). Le reste du bloc
   * (rembourrage 12, écart 10, pastille 34) vient du même relevé — il lui manquait cette mesure-ci.
   */
  marginTop: 8,
  padding: 12,
  gap: 10,
  avatar: 34,
  avatarFontSize: 12.5,
  nameSize: 12.5,
  nameWeight: 600,
  roleSize: 11,
  /**
   * ⚠️ EXPLICITE, ET C'EST TOUT L'INTÉRÊT : sans elle, les deux lignes héritent de la hauteur de ligne
   * du CORPS de l'app. La Console est la seule à en fixer une (`font: 14px/1.5`), ce qui portait son
   * bloc de texte à 35,25 px — juste au-dessus de l'avatar de 34 qui commande la hauteur partout
   * ailleurs. Son pied mesurait donc 60,3 px contre 59 pour les trois autres. Personne n'avait décidé
   * ce pixel et demi : une primitive partagée ne doit pas changer de taille selon la feuille qui
   * l'entoure. 1,2 reproduit ce que rendent aujourd'hui les trois surfaces qui s'accordent.
   */
  lineHeight: 1.2,
  buttonSize: 34,
  /**
   * ⚠️ LE CRAN DE LA CHARTE (`--r-ctrl`, 10 px), et non un littéral : la maquette pose
   * `border-radius:var(--r-ctrl)` sur ce bouton, comme sur les autres contrôles du rail. La valeur
   * figée à 9 px le distinguait d'un pixel de tous ses voisins — invisible seul, sensible en série.
   */
  buttonRadius: 'var(--r-ctrl)',
  iconSize: 17,
  iconStroke: 2,
  /**
   * LE PIED QUI OUVRE « MON COMPTE » (back-office) — relevé de la maquette : le nom et l'avatar deviennent un bouton
   * (rembourrage 4, décalé de -4 à gauche), le conteneur passe à `8px 8px 8px 12px` et l'écart avec la déconnexion à 6.
   * ⚠️ LES SOMMES TOMBENT SUR LE PIED ORDINAIRE : 8 + 4 = 12 en haut et en bas, et l'avatar reste à 12 du bord — le pied
   *    garde donc la même hauteur et la même colonne que dans les applications qui n'ouvrent pas de compte.
   */
  account: { padTop: 8, padRight: 8, padBottom: 8, padLeft: 12, gap: 6, buttonPad: 4 },
} as const;

/** Gamme de tailles de BOUTONS web (décision 2026-08-05 : fin des 26→40px selon l'app).
 *  Relevés : maquette Console 28-38 (38 dominant), EDI 28/30/34, Store ~40.
 *
 *  ⚠️ Cran `xs` AJOUTÉ (2026-08-15, maquette back-office) : les barres de filtres denses — journal,
 *  état du stock, articles, réceptions — travaillent à 32 px, et ni cette gamme ni `.field-opt` ne
 *  l'exposaient comme cran NOMMÉ. `.field-opt` le posait donc en littéral, et chaque nouvelle barre
 *  de filtres recopiait le nombre. Un cran sans nom se recopie ; un cran nommé se consomme.
 *
 *  ⚠️ Ces hauteurs sont AUSSI émises en `--h-xs/sm/md/lg` (theme-web) : elles n'existaient qu'en TS,
 *  donc tout CSS d'app devait recopier 30/36/40 à la main. */
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';
export const BUTTON: Record<ButtonSize, { height: number; padH: number; fontSize: number; radius: number }> = {
  xs: { height: 32, padH: 12, fontSize: 12, radius: 10 },
  sm: { height: 30, padH: 13, fontSize: 12.5, radius: 9 },
  md: { height: 36, padH: 16, fontSize: 13, radius: 10 },
  lg: { height: 40, padH: 18, fontSize: 13.5, radius: 11 },
};
export const BUTTON_WEIGHT = 600;
